import { useCallback, useEffect, useState } from 'react';
import { ApiError, useApi } from './client';

/** Mirrors `OverviewResponse` in `chancey-api/src/metrics.ts`. */
export interface AdminOverview {
  generatedAt: string;
  pricing: {
    inputUsdPerMtok: number;
    cachedInputUsdPerMtok: number;
    outputUsdPerMtok: number;
    model: string;
    resolvedFromModelTable: boolean;
  };
  rateLimits: { perHour: number; perDay: number };
  allTime: {
    scans: number;
    okScans: number;
    tokensIn: number;
    tokensOut: number;
    costUsd: number;
    uniqueUsers: number;
  };
  last30Days: Array<{
    day: string;
    scans: number;
    okScans: number;
    tokensIn: number;
    tokensOut: number;
    costUsd: number;
  }>;
  topUsers: Array<{
    userId: string;
    scans: number;
    tokensIn: number;
    tokensOut: number;
    costUsd: number;
    lastSeen: string | null;
  }>;
  errorsByCode: Array<{ code: string; count: number }>;
}

export interface AdminState {
  loading: boolean;
  error: string | null;
  data: AdminOverview | null;
  /**
   * Refetch the overview. Pass `true` to force the server to bust its cache
   * (deletes `mcache:overview`, then rebuilds).
   */
  reload: (force?: boolean) => void;
}

/**
 * Fetches the admin overview and provides a `reload()` to refetch, optionally
 * forcing a server-side cache bust. Data stays visible across reloads so the
 * UI doesn't flicker between refreshes.
 */
export function useAdminOverview(): AdminState {
  const api = useApi();
  const [data, setData] = useState<AdminOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [req, setReq] = useState<{ id: number; force: boolean }>({ id: 0, force: false });

  useEffect(() => {
    const ctrl = new AbortController();
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        if (req.force) {
          await api.post<void>('/v1/admin/overview/refresh', null, { signal: ctrl.signal });
        }
        const path = req.force ? '/v1/admin/overview?force=1' : '/v1/admin/overview';
        const res = await api.get<AdminOverview>(path, { signal: ctrl.signal });
        if (cancelled) return;
        setData(res);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.code === 'aborted') return;
        const msg =
          err instanceof ApiError && err.status === 403
            ? 'You do not have admin access.'
            : err instanceof Error
            ? err.message
            : 'Failed to load overview';
        setError(msg);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      ctrl.abort();
    };
  }, [api, req]);

  const reload = useCallback((force = false) => {
    setReq((prev) => ({ id: prev.id + 1, force }));
  }, []);

  return { loading, error, data, reload };
}

/**
 * Lightweight whoami call — lets the app show/hide the Admin nav item without having to
 * fetch the full overview. Swallows errors (treats failure as "not admin").
 */
export function useIsAdmin(): { loading: boolean; isAdmin: boolean } {
  const api = useApi();
  const [state, setState] = useState<{ loading: boolean; isAdmin: boolean }>({
    loading: true,
    isAdmin: false,
  });

  useEffect(() => {
    const ctrl = new AbortController();
    let cancelled = false;
    api
      .get<{ isAdmin: boolean }>('/v1/admin/whoami', { signal: ctrl.signal })
      .then((res) => {
        if (cancelled) return;
        setState({ loading: false, isAdmin: !!res.isAdmin });
      })
      .catch(() => {
        if (cancelled) return;
        setState({ loading: false, isAdmin: false });
      });
    return () => {
      cancelled = true;
      ctrl.abort();
    };
  }, [api]);

  return state;
}
